import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { SPONSORS, AMBASSADORS } from "../../data/site";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { Logo } from "../Logo";

const FOOTER_COLS = [
  {
    id: "gaming",
    title: "Gaming",
    links: [
      { label: "Casino", to: "/casino" },
      { label: "Slots", to: "/slots" },
      { label: "Crash", to: "/crash" },
      { label: "Table", to: "/table" },
      { label: "Fishing", to: "/fishing" },
      { label: "Favourite", to: "/favourite" },
      { label: "Lottery", to: "/lottery" },
    ],
  },
  {
    id: "about",
    title: "About Maxlive88",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Responsible Gaming", to: "/responsible" },
      { label: "KYC", to: "/kyc" },
    ],
  },
  {
    id: "features",
    title: "Features",
    links: [
      { label: "Promotions", to: "/promotions" },
      { label: "VIP Club", to: "/vip" },
      { label: "Referral", to: "/referral" },
      { label: "Brand Ambassadors", to: "/ambassadors" },
      { label: "APP Download", to: "/app-download" },
    ],
  },
  {
    id: "help",
    title: "Help",
    links: [
      { label: "Contact Us", to: "/contact" },
      { label: "Maxlive88 Forum", to: "/forum" },
      { label: "New Member Guide", to: "/guide" },
    ],
  },
];

// Default social channels — fallback when admin hasn't customised the list
const DEFAULT_SOCIALS = [
  { id: "facebook", label: "Facebook", slug: "facebook", color: "#1877f2", gradient: "", url: "", enabled: true, order: 1 },
  { id: "instagram", label: "Instagram", slug: "instagram", color: "#e1306c", gradient: "linear-gradient(135deg,#feda77 0%,#f58529 25%,#dd2a7b 50%,#8134af 75%,#515bd4 100%)", url: "", enabled: true, order: 2 },
  { id: "imo", label: "IMO", slug: "imo", color: "#19a8e1", gradient: "", url: "", enabled: true, order: 3 },
  { id: "tiktok", label: "TikTok", slug: "tiktok", color: "#000000", gradient: "linear-gradient(135deg,#25f4ee 0%,#000000 50%,#fe2c55 100%)", url: "", enabled: true, order: 4 },
  { id: "x", label: "X", slug: "x", color: "#000000", gradient: "", url: "", enabled: true, order: 5 },
  { id: "pinterest", label: "Pinterest", slug: "pinterest", color: "#bd081c", gradient: "", url: "", enabled: true, order: 6 },
  { id: "youtube", label: "YouTube", slug: "youtube", color: "#ff0000", gradient: "", url: "", enabled: true, order: 7 },
  { id: "telegram", label: "Telegram", slug: "telegram", color: "#26a5e4", gradient: "", url: "", enabled: true, order: 8 },
  { id: "whatsapp", label: "WhatsApp", slug: "whatsapp", color: "#25d366", gradient: "", url: "", enabled: true, order: 9 },
];

const FooterAccordion = ({ col, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="lg:hidden border-b border-white/10">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`footer-acc-${col.id}`}
        className="w-full flex items-center justify-between py-4 text-[#19a55c] font-bold text-base"
      >
        {col.title}
        <ChevronDown
          size={20}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul className="pb-4 space-y-2.5">
          {col.links.map((l) => (
            <li key={l.label}>
              <Link to={l.to} className="text-white/60 hover:text-white text-sm">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FooterColumn = ({ col }) => (
  <div className="hidden lg:block">
    <h4 className="text-[#19a55c] font-bold text-base mb-4">{col.title}</h4>
    <ul className="space-y-2.5">
      {col.links.map((l) => (
        <li key={l.label}>
          <Link to={l.to} className="text-white/60 hover:text-white text-sm transition-colors">
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const SponsorMiniCard = ({ s }) => (
  <div className="flex items-start gap-2.5 sm:gap-3 min-w-0" data-testid={`footer-sponsor-${s.id}`}>
    <img src={s.img} alt={s.name} className="h-10 w-10 sm:h-12 sm:w-12 object-contain shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-white font-bold text-[13px] sm:text-sm leading-tight line-clamp-2">{s.name}</p>
      <p className="text-[11px] sm:text-xs text-white/55 mt-1 leading-snug line-clamp-2">{s.role}</p>
    </div>
  </div>
);

const SIG_TEXT = {
  mia: "Mia K. ♡",
  kp: "K. Pietersen",
  amy: "Amy J.",
  hansika: "Hansika",
  wasim: "W. Akram",
  keya: "Keya",
  yesha: "Yesha S.",
};

const AmbassadorRow = ({ a }) => (
  <div className="flex items-center gap-2.5 sm:gap-3 min-w-0" data-testid={`footer-amb-${a.id}`}>
    <span
      className="font-signature text-lg sm:text-2xl text-white/85 shrink-0 w-16 sm:w-20 text-center leading-none italic"
      style={{ textShadow: "0 1px 0 rgba(255,255,255,0.05)" }}
    >
      {SIG_TEXT[a.id] || a.name.split(" ")[0]}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-white font-bold text-[13px] sm:text-sm leading-tight truncate">{a.name}</p>
      <p className="text-[11px] sm:text-xs text-white/55">{a.year}</p>
    </div>
  </div>
);

// Replace {brand} placeholder inside legal paragraphs with a styled span
const renderLegalParagraph = (text, brand) => {
  const parts = String(text || "").split("{brand}");
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <span className="text-[#19a55c] font-semibold">{brand}</span>
      )}
    </span>
  ));
};

export const SiteFooter = () => {
  const [showMore, setShowMore] = useState(false);
  const { settings } = useSiteSettings();

  const seoTitle = settings?.footer_seo_title || "Maxlive88 Bangladesh — Leading Online Gaming and Betting Platform in Bangladesh";
  const seoParagraphs = (settings?.footer_seo_paragraphs && settings.footer_seo_paragraphs.length > 0)
    ? settings.footer_seo_paragraphs
    : [
        "In recent years, the online gaming and betting industry in Bangladesh has seen exponential growth, attracting players who seek excitement and rewarding experiences. As more people embrace digital platforms, the demand for reliable and diverse gaming options has surged.",
        "Our platform stands out as a top choice, offering an extensive range of games and betting opportunities from renowned providers worldwide including Evolution, Pragmatic Play, JILI, Microgaming, Spribe and many more. Whether you're into sports betting, casino games, or unique online gaming experiences, we've got you covered.",
        "Join thousands of players across South Asia for an unmatched gaming experience with a generous 100% welcome bonus, daily cashbacks, VIP perks, and 24/7 live support.",
      ];
  const brandTagline = settings?.footer_brand_tagline || "Play Smart, Win Big";
  const copyright = settings?.footer_copyright || "© 2026 Maxlive88 Copyrights. All Rights Reserved";
  const legalBrand = settings?.footer_legal_brand || "Maxlive88.com";
  const legalEmail = settings?.footer_legal_email || "legal@maxlive88.com";
  const legalParagraphs = (settings?.footer_legal_paragraphs && settings.footer_legal_paragraphs.length > 0)
    ? settings.footer_legal_paragraphs
    : [
        "{brand} is owned and operated by Northern Lights Limited Holdings Limited. registration number: 15839, registered address: Hamchhako, Mutsamudu, Autonomous Island of Anjouan, Union of Comoros.",
        "{brand} is licensed and regulated by the Government of the Autonomous Island of Anjouan, Union of Comoros and operates under License No. ALSI-202410030-FI1. {brand} has passed all regulatory compliance and is legally authorized to conduct gaming operations for any and all games of chance and wagering.",
      ];

  return (
    <footer
      data-testid="site-footer"
      className="mt-4 sm:mt-8 border-t border-white/5"
      style={{ background: "#070b11" }}
    >
      <div className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-8 pt-3 sm:pt-6 lg:pt-10 pb-6">
        {/* Mobile accordions */}
        <div className="lg:hidden">
          {FOOTER_COLS.map((c) => (
            <FooterAccordion key={c.id} col={c} />
          ))}
        </div>

        {/* Desktop columns */}
        <div className="hidden lg:grid grid-cols-4 gap-8">
          {FOOTER_COLS.map((c) => (
            <FooterColumn key={c.id} col={c} />
          ))}
        </div>

        {/* Sponsorships */}
        <section className="mt-8 pb-6 border-b border-white/10">
          <h3 className="text-[#19a55c] font-bold text-base mb-4">Sponsorships</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 gap-y-4 sm:gap-y-5">
            {SPONSORS.map((s) => (
              <SponsorMiniCard key={s.id} s={s} />
            ))}
          </div>
        </section>

        {/* Brand Ambassadors */}
        <section className="mt-6 pb-6 border-b border-white/10">
          <h3 className="text-[#19a55c] font-bold text-base mb-4">Brand Ambassadors</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 gap-y-4 sm:gap-y-5">
            {AMBASSADORS.map((a) => (
              <AmbassadorRow key={a.id} a={a} />
            ))}
          </div>
        </section>

        {/* Gaming License */}
        <section className="mt-6 pb-6 border-b border-white/10">
          <h3 className="text-[#19a55c] font-bold text-base mb-4">Gaming License</h3>
          <div className="flex flex-wrap items-center gap-4">
            <img
              src="https://img.b112j.com/bj/h5/assets/images/footer/gaming_license.png"
              alt="Gaming Curacao"
              className="h-10 w-auto"
              loading="lazy"
            />
            <img
              src="https://img.b112j.com/bj/h5/assets/images/footer/anjouan_license.png"
              alt="Anjouan eGaming"
              className="h-10 w-auto"
              loading="lazy"
            />
            <img
              src="https://img.b112j.com/bj/h5/assets/images/footer/montenegro_license.png"
              alt="Montenegro"
              className="h-10 w-auto"
              loading="lazy"
            />
          </div>
        </section>

        {/* Responsible Gaming */}
        <section className="mt-6 pb-6 border-b border-white/10">
          <h3 className="text-[#19a55c] font-bold text-base mb-4">Responsible Gaming</h3>
          <div className="flex items-center gap-4">
            <img
              src="https://img.b112j.com/bj/h5/assets/v3/images/icon-set/trivial-type/regulations.svg"
              alt="Regulations"
              className="h-10 w-auto opacity-80"
              loading="lazy"
            />
            <img
              src="https://img.b112j.com/bj/h5/assets/v3/images/icon-set/trivial-type/gamcare.svg"
              alt="GamCare"
              className="h-10 w-auto opacity-80"
              loading="lazy"
            />
            <img
              src="https://img.b112j.com/bj/h5/assets/v3/images/icon-set/trivial-type/age-limit.svg"
              alt="18+"
              className="h-10 w-auto opacity-80"
              loading="lazy"
            />
          </div>
        </section>

        {/* Social media — admin-controlled list, smaller fixed-size icons */}
        <section className="mt-6 pb-6 border-b border-white/10">
          <h3 className="text-[#19a55c] font-bold text-base mb-4">Follow us</h3>
          <div className="flex flex-wrap gap-2.5 sm:gap-3" data-testid="footer-socials">
            {(() => {
              const list = Array.isArray(settings?.social_links) && settings.social_links.length > 0
                ? settings.social_links
                : DEFAULT_SOCIALS;
              const visible = list
                .filter((s) => s && s.enabled !== false)
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              if (visible.length === 0) return null;
              return visible.map((s) => {
                const href = s.url || "";
                const isLive = !!href;
                const color = s.color || "#19a55c";
                return (
                  <a
                    key={s.id || s.label}
                    href={isLive ? href : "#"}
                    target={isLive ? "_blank" : undefined}
                    rel={isLive ? "noreferrer noopener" : undefined}
                    aria-label={s.label}
                    data-testid={`social-${s.id || s.slug || "x"}`}
                    onClick={(e) => { if (!isLive) e.preventDefault(); }}
                    className={`group relative h-10 w-10 sm:h-11 sm:w-11 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5 ${isLive ? "" : "opacity-55 cursor-default"}`}
                    style={{
                      background: s.gradient || color,
                      boxShadow: `0 4px 12px ${color}55, inset 0 -1px 2px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.2)`,
                    }}
                  >
                    <img
                      src={`https://cdn.simpleicons.org/${s.slug || "link"}/ffffff`}
                      alt=""
                      className="h-[48%] w-[48%] object-contain"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const sib = e.currentTarget.nextElementSibling;
                        if (sib) sib.style.display = "flex";
                      }}
                    />
                    <span
                      aria-hidden
                      className="absolute inset-0 hidden items-center justify-center text-white font-bold text-[10px] uppercase tracking-tight"
                    >
                      {String(s.label || "").slice(0, 2)}
                    </span>
                  </a>
                );
              });
            })()}
          </div>
        </section>

        {/* SEO text — admin controlled */}
        <section className="mt-6 pb-6 border-b border-white/10 relative">
          <h3 className="text-white font-bold text-lg sm:text-xl mb-3 leading-tight" data-testid="footer-seo-title">
            {seoTitle}
          </h3>
          <div
            className={`text-white/55 text-sm leading-relaxed space-y-3 ${showMore ? "" : "max-h-32 overflow-hidden relative"}`}
            data-testid="footer-seo-body"
          >
            {seoParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            {!showMore && (
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#070b11] to-transparent pointer-events-none"
              />
            )}
          </div>
          <div className="flex justify-center mt-3">
            <button
              type="button"
              onClick={() => setShowMore((s) => !s)}
              data-testid="show-more-btn"
              className="px-5 h-9 rounded-md bg-[#1a1d24] border border-white/10 text-sm text-white/80 hover:bg-[#222a3d]"
            >
              {showMore ? "Show less" : "Show more"}
            </button>
          </div>
        </section>

        {/* Brand tagline + copyright */}
        <section className="mt-6 pb-6 border-b border-white/10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <a href="/" data-testid="footer-logo-link" aria-label="home">
            <Logo className="text-3xl font-extrabold tracking-tight" />
          </a>
          <div className="min-w-0">
            <p className="text-[#19a55c] font-bold text-sm" data-testid="footer-brand-tagline">{brandTagline}</p>
            <p className="text-xs text-white/45 mt-1" data-testid="footer-copyright">{copyright}</p>
          </div>
        </section>

        {/* Legal text — admin controlled */}
        <section className="mt-6 text-xs text-white/45 leading-relaxed space-y-3" data-testid="footer-legal">
          {legalParagraphs.map((p, i) => (
            <p key={i}>{renderLegalParagraph(p, legalBrand)}</p>
          ))}
          {legalEmail && (
            <p>
              Contact us{" "}
              <a
                href={`mailto:${legalEmail}`}
                className="text-[#19a55c] hover:underline"
                data-testid="footer-legal-email"
              >
                {legalEmail}
              </a>
              .
            </p>
          )}
        </section>
      </div>
    </footer>
  );
};
